// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { APIService } from "../../utils/apiService";
import { AppConfig } from "../../config/config";
import { enqueueSnackbarMessage } from "../commonSlice/common";
import { SnackMessage } from "../../config/constant";
import axios, { HttpStatusCode } from "axios";
import { State } from "../../utils/types";

interface TeamsState {
  state: State;
  teams: string[] | null;
  errorMessage: string | null;
}

const initialState: TeamsState = {
  state: State.idle,
  teams: null,
  errorMessage: null,
};

export const fetchTeams = createAsyncThunk(
  "teams/fetchTeams",
  async (organization: string, { dispatch, rejectWithValue }) => {
    APIService.getCancelToken().cancel();
    const newCancelTokenSource = APIService.updateCancelToken();
    try {
      const response = await APIService.getInstance().get(AppConfig.serviceUrls.teams, {
        params: { organization },
        cancelToken: newCancelTokenSource.token,
      });
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue("Request canceled");
      }

      if (axios.isAxiosError(error)) {
        dispatch(
          enqueueSnackbarMessage({
            message:
              error.response?.status === HttpStatusCode.InternalServerError
                ? SnackMessage.error.fetchTeamsMessage
                : String(error.response?.data?.message || "Unknown error"),
            type: "error",
          })
        );
        return rejectWithValue(error.response?.data || "Failed to fetch teams");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

const teamsSlice = createSlice({
  name: "teams",
  initialState,
  reducers: {
    resetTeamsState(state) {
      state.state = State.idle;
      state.teams = null;
      state.errorMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTeams.pending, (state) => {
        state.state = State.loading;
        state.errorMessage = "Fetching teams...";
      })
      .addCase(fetchTeams.fulfilled, (state, action) => {
        state.state = State.success;
        state.teams = action.payload;
        state.errorMessage = null;
      })
      .addCase(fetchTeams.rejected, (state, action) => {
        state.state = State.failed;
        state.errorMessage = "Failed to fetch teams";
      });
  },
});

export const { resetTeamsState } = teamsSlice.actions;
export default teamsSlice.reducer;